import React, { useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import Sidebar from './Sidebar';
import BottomNav from './BottomNav';
import { usePopup } from '../context/PopupContext';
import api from '../wrapper/API';
import { validatePasswordChange, PASSWORD_REQUIREMENTS_TEXT } from '../../util/passwordPolicy';
import Popup from '../popup/Popup';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import useRegistrationsBadge from '../../hooks/useRegistrationsBadge';
import { isAdmin } from '../../util/auth';
import { ROUTES } from '../../util/routes';
import { useOffline } from '../context/OfflineContext';
import OfflineUnavailable from './OfflineUnavailable';
import '../form/Form.css';
import '../popup/EditPopup.css';
import './AppShell.css';

// Seiten, die offline funktionieren (lokale Kopie oder rein clientseitig). Alle anderen zeigen im
// Offline-Modus einen Hinweis statt einer leeren Seite. Wiki-Seiten (/wiki/:id) lesen die lokale Kopie
// aller Wikis.
const OFFLINE_CAPABLE = new Set([
  ROUTES.HOME, ROUTES.PRICES,
  ROUTES.DOCS_ORGA, ROUTES.DOCS_ADMIN,
]);
function isOfflineCapable(pathname) {
  return OFFLINE_CAPABLE.has(pathname)
    || pathname.startsWith(ROUTES.EVENT_BASE + '/')
    || pathname.startsWith(ROUTES.WIKI_BASE + '/');
}

function AppShell() {
  const [collapsed, setCollapsed] = useState(
    localStorage.getItem('sidebarCollapsed') === 'true'
  );
  const location = useLocation();
  const { closePopup } = usePopup();

  // Offline-Hinweis nur für Seiten, die im Offline-Modus BETRETEN werden. Eine bereits geöffnete Seite
  // wird beim Wechsel in den Offline-Modus nicht ersetzt — ungespeicherte Eingaben bleiben erhalten.
  const { isOffline } = useOffline();
  const [pageEntry, setPageEntry] = useState({ path: location.pathname, offline: isOffline });
  if (pageEntry.path !== location.pathname) {
    setPageEntry({ path: location.pathname, offline: isOffline });
  } else if (pageEntry.offline && !isOffline) {
    setPageEntry({ path: pageEntry.path, offline: false });
  }
  const showOfflinePlaceholder = isOffline && pageEntry.offline
    && pageEntry.path === location.pathname && !isOfflineCapable(location.pathname);

  const oldPasswordRef = useRef();
  const newPasswordRef = useRef();
  const newRepeatPasswordRef = useRef();
  const [pwError, setPwError] = useState(undefined);

  const { isManager, isSupported, isSubscribed, subscribe, unsubscribe, error: pushError } = usePushNotifications();
  const admin = isAdmin();
  const registrationsBadge = useRegistrationsBadge({ enabled: admin });

  const handleChangePasswordConfirm = () => {
    const error = validatePasswordChange(newPasswordRef.current.value, newRepeatPasswordRef.current.value);
    if (error) { setPwError(error); return; }

    api.put('/Login/ChangePassword', {
      oldPassword: oldPasswordRef.current.value,
      newPassword: newPasswordRef.current.value,
    })
      .then(() => { setPwError(undefined); closePopup(); })
      .catch((err) => setPwError(err.response?.data));
  };

  const handlePushConfirm = async () => {
    if (isSubscribed) {
      const ok = await unsubscribe();
      if (ok) closePopup();
    } else {
      const ok = await subscribe();
      if (ok) closePopup();
    }
  };

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('sidebarCollapsed', String(next));
  };

  return (
    <div className="appShell">
      <div className="shellSidebarSlot">
        <Sidebar
          collapsed={collapsed}
          onToggle={toggleCollapsed}
          isSubscribed={isSubscribed}
          registrationsBadge={registrationsBadge}
        />
      </div>
      <div className="shellContent">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
          >
            {showOfflinePlaceholder ? <OfflineUnavailable /> : <Outlet />}
          </motion.div>
        </AnimatePresence>
      </div>
      <div className="shellBottomNavSlot">
        <BottomNav
          isSubscribed={isSubscribed}
          registrationsBadge={registrationsBadge}
        />
      </div>
      <Popup
        name="editpassword"
        confirmText="Übernehmen"
        onConfirm={handleChangePasswordConfirm}
        withCancel
        dontCloseOnConfirm
        type="info"
      >
        <h3>Passwort ändern:</h3>
        <input required type="password" ref={oldPasswordRef} className="textInput" placeholder="Altes Passwort" />
        <input required type="password" ref={newPasswordRef} className="textInput" placeholder="Neues Passwort" />
        <input required type="password" ref={newRepeatPasswordRef} className="textInput" placeholder="Neues Passwort wiederholen" />
        <p className="passwordHint">{PASSWORD_REQUIREMENTS_TEXT}</p>
        {pwError && <p className="newError">{pwError}</p>}
      </Popup>
      <Popup
        name="pushnotifications"
        confirmText={isSubscribed ? 'Deaktivieren' : 'Aktivieren'}
        onConfirm={handlePushConfirm}
        withCancel
        withConfirm={isSupported}
        dontCloseOnConfirm
        type="info"
      >
        <h3>Push-Benachrichtigungen</h3>
        {!isSupported
          ? <p className="newError">Dein Browser unterstützt keine Push-Benachrichtigungen.</p>
          : <>
              <p>
                {isSubscribed
                  ? 'Auf diesem Gerät aktiv. Du bekommst:'
                  : 'Aktiviere Benachrichtigungen auf diesem Gerät. Du bekommst dann:'}
              </p>
              {/* Was ankommt, entscheidet die Rolle beim Versand — hier nur die Übersicht dazu. */}
              <ul className="pushInfoList">
                <li>
                  <span className="pushInfoRole">Als Spieltagleiter</span>
                  3 Tage vorher und am Vorabend (je 18 Uhr) eine Erinnerung — am Vorabend mit dem Hinweis, die Teilnehmerliste offline zu laden.
                </li>
                {isManager && (
                  <li>
                    <span className="pushInfoRole">Als Anmelde-Manager</span>
                    Neue Anmeldungen sofort, offene Anmeldungen täglich um 17 Uhr und 2 Tage sowie 1 Tag vor jedem Event (16 Uhr) einen Check mit dem aktuellen Stand.
                  </li>
                )}
              </ul>
              {pushError && <p className="newError">{pushError}</p>}
            </>
        }
      </Popup>
    </div>
  );
}

export default AppShell;
